import { useMemo } from 'react';
import { useAvContext } from '../../../context/AvContextProvider';
import { useIsFreaProvisioned } from '../../../FREA/views/FREASettings/hooks';
import { AppPathsValue, PAGE_PATHS, PathFeatureFlag, Permission } from '../../../types';
import { MenuItem, SubMenuItem } from '../../../types/sideBar.types';
import { isVisibleByFeatureFlags } from '../navigation.utils';
import { appsSideBar } from '../sidebar.constants';

/**
 * `appsSideBar` reduced to the items the current user may see: back-office, role, permission and
 * feature-flag guards, plus FREA provisioning for Frontier Exposure. Parents left without visible children are dropped.
 * Nested items are checked with their parent's `permission`.
 */
const useVisibleAppsSideBar = (): Record<string, MenuItem[]> => {
  const {
    featureFlags,
    userPermissions: { hasAllowedPermission, isAvalorInternalRole, isBackOfficeAvailable },
  } = useAvContext();

  const passesGuards = (item: SubMenuItem, app: string, permission = item.permission) =>
    (!item.isBackOffice || isBackOfficeAvailable) &&
    (!PathFeatureFlag[item.path] || !!featureFlags[PathFeatureFlag[item.path]]) &&
    isVisibleByFeatureFlags(item, featureFlags) &&
    (!!item.subMenu ||
      (item.internalRoleOnly
        ? isAvalorInternalRole
        : !!hasAllowedPermission({ path: item.permissionPath ?? item.path, app: app as AppPathsValue, permission })));

  const isFreaProvisioned = useIsFreaProvisioned(!!hasAllowedPermission({ path: PAGE_PATHS.FREA_SETTINGS }));

  return useMemo(() => {
    const filterItems = (items: SubMenuItem[], app: string, parentPermission?: Permission): SubMenuItem[] =>
      items.flatMap(item => {
        if (
          !passesGuards(item, app, parentPermission ?? item.permission) ||
          (item.path === PAGE_PATHS.FREA_SETTINGS && !isFreaProvisioned)
        ) {
          return [];
        }
        if (!item.subMenu) {
          return [item];
        }
        const subMenu = filterItems(item.subMenu, app, item.permission);
        return subMenu.length ? [{ ...item, subMenu }] : [];
      });

    return Object.fromEntries(
      Object.entries(appsSideBar).map(([app, menus]) => [
        app,
        menus
          .filter(menu => isVisibleByFeatureFlags(menu, featureFlags))
          .map(menu => ({ ...menu, subMenu: filterItems(menu.subMenu, app) })),
      ])
    );
  }, [featureFlags, hasAllowedPermission, isAvalorInternalRole, isBackOfficeAvailable, isFreaProvisioned]);
};

export default useVisibleAppsSideBar;
